import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";

import { PROJECT_STATUS_COLOURS, type ProjectStatus } from "./projectStatus";
import { useProjectStatusEnabled } from "./projectStatusAvailability";

interface ProjectStatusBadgeProps {
  status: ProjectStatus;
  /** "chip" (default): dot + label pill. "dot": the bare blip, for tight rows. */
  variant?: "chip" | "dot";
  className?: string;
}

/**
 * The coloured blip showing whether a project is active or inactive. Markup
 * mirrors the PM status chip (`StatusFormDialog`'s preview). A `<span>`
 * throughout, because it renders inside buttons (project cards, picker rows).
 * Project choices put the bare `dot` BEFORE every project's name; the labelled
 * chip is for the Buildings list rows; the dashboard tiles use ProjectStatusPill.
 */
export function ProjectStatusBadge({
  status,
  variant = "chip",
  className,
}: ProjectStatusBadgeProps) {
  const { t } = useLanguage();
  // Dormant until the database has the column — every status reads "active"
  // then, and a row of green "Active" chips would announce a feature that isn't
  // live yet.
  const enabled = useProjectStatusEnabled();
  if (!enabled) return null;
  const colours = PROJECT_STATUS_COLOURS[status];
  const label = status === "inactive" ? t.projects.status.inactive : t.projects.status.active;

  if (variant === "dot") {
    return (
      <span
        role="img"
        aria-label={label}
        title={label}
        className={cn("inline-block h-2 w-2 shrink-0 rounded-full", className)}
        style={{ backgroundColor: colours.dotColor }}
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
        className
      )}
      style={{ color: colours.textColor, backgroundColor: colours.bgColor }}
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: colours.dotColor }}
      />
      {label}
    </span>
  );
}
